import { createSSRApp, watch } from "vue";
import App from "./App.vue";
import router from "./router";
import { createI18n } from "vue-i18n";
import "@fortawesome/fontawesome-free/css/fontawesome.css";
import "@fortawesome/fontawesome-free/css/solid.css";
import "@fortawesome/fontawesome-free/css/regular.css";
import "vue3-toastify/dist/index.css";
import Vue3Toastify from "vue3-toastify";
import { createHead } from "@unhead/vue";
import store from "./store";
import config from "@/config.js";
import { buildCanonical } from "@/utils/canonical.cjs";

const i18n = createI18n({
  legacy: false,
  locale: "en",
  fallbackLocale: "en",
  messages: {},
});

const app = createSSRApp(App);
const head = createHead();

app.use(router);
app.use(store);
app.use(i18n);
app.use(Vue3Toastify, {
  autoClose: 3000,
  limit: 3,
  clearOnUrlChange: true,
  style: {
    opacity: "1",
    userSelect: "initial",
  },
});
app.use(head);
const initialData =
  typeof window !== "undefined"
    ? window.__INITIAL_DATA__ || null
    : null;

app.provide("ssrInitialData", initialData);

// Canonical is injected server-side on full page loads; update on SPA navigations only.
const CANONICAL_BASE =
  process.env.VUE_APP_HOST || "https://www.credencedata.com";

function updateCanonical(fullPath) {
  const href = buildCanonical(fullPath, CANONICAL_BASE);
  head.push({
    link: [{ rel: "canonical", href, key: "canonical" }],
  });
  if (typeof window !== "undefined") {
    window.__CANONICAL_URL__ = href;
  }
}

router.afterEach((to) => updateCanonical(to.fullPath));

let translationsCdn = config.translationsProdCdn;
if (
  process.env.NODE_ENV === "development" ||
  process.env.NODE_ENV === "staging"
) {
  translationsCdn = config.translationsStgCdn;
}

async function loadPageTranslation(routeName, locale) {
  try {
    const jsonUrl = `${translationsCdn}common/${locale}.json`;
    const jsonUrl1 = `${translationsCdn}${routeName}/${locale}.json`;

    const [response, response1] = await Promise.all([
      fetch(jsonUrl),
      fetch(jsonUrl1),
    ]);

    const [data, data1] = await Promise.all([
      response.json(),
      response1.json(),
    ]);
    i18n.global.setLocaleMessage(locale, {
      ...data,
      ...i18n.global.getLocaleMessage(locale),
      ...data1,
    });
    i18n.global.locale.value = locale;
  } catch (error) {
    console.warn(
      `No translation file found for page: ${routeName}, locale: ${locale}`
    );
    fallbackEnglish(locale, routeName);
  }
}

const fallbackEnglish = async (locale, routeName) => {
  // Fallback to local English JSON
  try {
    const commonEN = await import(`../locales/common/en.json`);
    const routeEN = await import(`../locales/${routeName}/en.json`);
    i18n.global.setLocaleMessage(locale, {
      ...commonEN,
      ...i18n.global.getLocaleMessage(locale),
      ...routeEN,
    });
    i18n.global.locale.value = "en";
    if (!localStorage.getItem("errorToastShowedOnce")) {
      // Show toast message, if converted back to english
      store.dispatch("addToast", {
        message: "Language selection is not available in offline mode.",
        type: "error",
      });
      localStorage.setItem("errorToastShowedOnce", true);
    }
  } catch (localError) {
    console.error("Failed to load local English JSON:", localError);
  }
};

router.beforeEach(async (to, from, next) => {
  const locale = i18n.global.locale.value;
  await loadPageTranslation(to.name, locale);
  next();
});

watch(
  () => i18n.global.locale.value,
  async (newLocale) => {
    const currentRoute = router.currentRoute.value;
    if (currentRoute && currentRoute.name) {
      if (newLocale == "en") {
        const selectedLanguage = {
          code: "en",
          language: "English",
          region: "Global",
          flag: "gb",
        };
        i18n.global.locale.value = newLocale;
        store.dispatch("updateSelectedLanguage", selectedLanguage);
      }
      await loadPageTranslation(currentRoute.name, newLocale);
    }
  }
);

router.isReady().then(() => {
  app.mount("#app");
});